import { Calendar } from 'lucide-react'
import React, { useCallback, useRef, useState } from 'react'

interface DatePickerProps {
  id?: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  label?: string
  min?: string | undefined
  max?: string | undefined
}

export default function DatePicker({
  id,
  value,
  onChange,
  placeholder = 'dd/mm/yyyy',
  className = '',
  label,
  min,
  max,
}: DatePickerProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isFocused, setIsFocused] = useState(false)

  const formatDateForDisplay = (dateString: string): string => {
    if (!dateString) return ''
    try {
      const date = new Date(dateString + 'T00:00:00')
      if (isNaN(date.getTime())) return dateString

      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      return `${day}/${month}/${year}`
    } catch {
      return dateString
    }
  }

  const openPicker = useCallback(() => {
    const input = inputRef.current
    if (!input) return

    if (typeof input.showPicker === 'function') {
      input.showPicker()
    } else {
      input.focus()
    }
  }, [])

  const handleCalendarIconClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    openPicker()
  }

  return (
    <div className='relative'>
      {label && (
        <label
          htmlFor={id}
          className='mb-1 block text-xs font-medium text-gray-600'
        >
          {label}
        </label>
      )}
      <div
        className={`relative flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm transition-all ${
          isFocused
            ? 'border-gray-400 ring-1 ring-gray-400'
            : 'hover:border-gray-400'
        } ${className}`}
      >
        <button
          type='button'
          onClick={openPicker}
          className='flex-1 text-left'
        >
          <span className={value ? 'text-gray-900' : 'text-gray-400'}>
            {value ? formatDateForDisplay(value) : placeholder}
          </span>
        </button>
        <button
          type='button'
          onClick={handleCalendarIconClick}
          className='relative z-10 ml-2 flex items-center justify-center rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors'
          aria-label='Open date picker'
          tabIndex={-1}
        >
          <Calendar className='h-4 w-4' />
        </button>
        <input
          ref={inputRef}
          id={id}
          type='date'
          value={value}
          onChange={e => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          min={min}
          max={max}
          className='absolute left-0 top-0 h-full w-[calc(100%-2.5rem)] cursor-pointer opacity-0'
          style={{ fontSize: '16px' }}
        />
      </div>
    </div>
  )
}

